<template>
  <b-modal
      :visible="visible"
      size="xl"
      hide-footer
      centered
      title="Постеры"
      @change="$emit('change', $event)"
      @shown="onShown"
      @hidden="onHidden"
  >
    <b-overlay :show="isLoading" rounded="sm">
      <!-- а) Лента сохранённых постеров -->
      <div v-if="posters.length" class="mb-2">
        <div class="d-flex align-items-center" style="gap: 0.5rem;">
          <b-button
              variant="flat-secondary"
              class="btn-icon flex-shrink-0"
              :disabled="viewIndex === 0"
              @click="goPrev"
          >
            <feather-icon icon="ChevronLeftIcon" size="28" />
          </b-button>

          <div class="poster-strip flex-grow-1">
            <div
                v-for="item in visiblePosters"
                :key="item.poster.id"
                class="poster-strip-item"
                :class="{ 'poster-strip-item-active': item.index === viewIndex }"
                @click="setActivePoster(item.index)"
            >
              <b-img
                  :src="item.index === viewIndex
                    ? (item.poster.s3_url || item.poster.url)
                    : (item.poster.preview_url || item.poster.s3_url || item.poster.url)"
              />
            </div>
          </div>

          <b-button
              variant="flat-secondary"
              class="btn-icon flex-shrink-0"
              :disabled="viewIndex >= posters.length - 1"
              @click="goNext"
          >
            <feather-icon icon="ChevronRightIcon" size="28" />
          </b-button>
        </div>

        <div class="text-center text-muted small mt-50 mb-1">
          {{ viewIndex + 1 }} / {{ posters.length }}
        </div>

        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="d-flex align-items-center flex-wrap">
            <b-badge variant="light-secondary" class="mr-50 mb-50">
              {{ sourceLabel(currentPoster.source) }}
            </b-badge>
            <b-badge v-if="currentPoster.s3_path" variant="light-info" class="mr-50 mb-50">
              S3
            </b-badge>
          </div>
          <div class="d-flex align-items-center flex-wrap">
            <b-form-select
                :value="currentPoster.type"
                :options="posterTypeOptions"
                size="sm"
                style="min-width: 200px;"
                class="mr-50 mb-50"
                @change="value => updatePosterType(currentPoster, value)"
            />
            <b-button
                v-if="!currentPoster.s3_path"
                variant="outline-primary"
                size="sm"
                class="mr-50 mb-50"
                :disabled="isDownloading"
                @click="downloadCurrentPoster"
            >
              <b-spinner v-if="isDownloading" small class="mr-50" />
              <feather-icon v-else icon="DownloadIcon" class="mr-50" />
              <span>Скачать на S3</span>
            </b-button>
            <b-button
                variant="flat-danger"
                size="sm"
                class="mb-50"
                :disabled="isDeleting"
                @click="deleteCurrentPoster"
            >
              <feather-icon icon="TrashIcon" class="mr-50" />
              <span>Удалить</span>
            </b-button>
          </div>
        </div>

        <div class="mt-50">
          <b-form-checkbox
              v-model="currentPoster.has_director_name"
              switch
              @change="value => updatePosterFlag(currentPoster, 'has_director_name', value)"
          >
            Имя режиссёра на постере
          </b-form-checkbox>
          <b-form-checkbox
              v-model="currentPoster.has_actor_names"
              switch
              class="mt-50"
              @change="value => updatePosterFlag(currentPoster, 'has_actor_names', value)"
          >
            Имена актёров на постере
          </b-form-checkbox>
        </div>
      </div>
      <div v-else-if="!isLoading" class="text-center text-muted py-2">
        Постеров пока нет
      </div>

      <hr>

      <!-- б) Подбор из источников -->
      <div class="mb-2">
        <h6 class="mb-1">Подбор из источников</h6>
        <div class="d-flex flex-wrap mb-1">
          <b-button
              v-for="source in sourceButtons"
              :key="source.value"
              variant="outline-secondary"
              size="sm"
              class="mr-50 mb-50"
              :disabled="isLoadingPreview"
              @click="fetchPreview(source.value)"
          >
            <feather-icon :icon="source.icon" class="mr-50" />
            <span>{{ source.label }}</span>
          </b-button>
        </div>

        <div v-if="isLoadingPreview" class="text-center mb-2">
          <b-spinner label="Loading..." />
        </div>
        <template v-else-if="previewSource">
          <div v-if="previewItems.length" class="posters-grid mb-1">
            <div
                v-for="(item, index) in previewItems"
                :key="index"
                class="poster-item-wrapper"
            >
              <div
                  class="poster-item"
                  :class="{'poster-item-selected': isPreviewSelected(index)}"
                  @click="togglePreviewSelection(index)"
              >
                <b-img
                    :src="item.preview_url || item.url"
                    class="poster-img"
                    :class="{'grayscale': item.exists}"
                    loading="lazy"
                />
              </div>
              <b-badge
                  v-if="item.exists"
                  variant="secondary"
                  class="poster-corner-badge"
              >
                В базе
              </b-badge>
              <div
                  v-else-if="isPreviewSelected(index)"
                  class="poster-selected-check"
              >
                <feather-icon icon="CheckIcon" size="14" />
              </div>
              <b-badge
                  v-if="item.lang"
                  variant="light-dark"
                  class="poster-lang-badge"
              >
                {{ item.lang }}
              </b-badge>
            </div>
          </div>
          <div v-else class="text-center text-muted mb-1">
            Источник не вернул постеров
          </div>

          <div v-if="previewItems.length" class="d-flex align-items-center flex-wrap">
            <b-form-select
                v-model="addType"
                :options="posterTypeOptions"
                size="sm"
                style="min-width: 200px;"
                class="mr-50 mb-50"
            />
            <b-button
                variant="primary"
                size="sm"
                class="mb-50"
                :disabled="!selectedPreviewIndexes.length || isAddingPosters"
                @click="addSelectedPosters"
            >
              <b-spinner v-if="isAddingPosters" small class="mr-50" />
              <feather-icon v-else icon="PlusIcon" class="mr-50" />
              <span>Добавить выбранные ({{ selectedPreviewIndexes.length }})</span>
            </b-button>
          </div>
        </template>
      </div>

      <hr>

      <!-- в) Ручная загрузка -->
      <div>
        <h6 class="mb-1">Ручная загрузка</h6>
        <div class="d-flex align-items-center flex-wrap">
          <b-form-file
              v-model="uploadFile"
              accept=".jpg,.jpeg,.png,.webp"
              size="sm"
              class="poster-file-input mr-50 mb-50"
              placeholder="Выбрать файл..."
              browse-text="Обзор"
          />
          <b-form-select
              v-model="uploadType"
              :options="posterTypeOptions"
              size="sm"
              style="min-width: 200px;"
              class="mr-50 mb-50"
          />
          <b-button
              variant="primary"
              size="sm"
              class="mb-50"
              :disabled="!uploadFile || isUploading"
              @click="uploadPoster"
          >
            <b-spinner v-if="isUploading" small class="mr-50" />
            <feather-icon v-else icon="UploadIcon" class="mr-50" />
            <span>Загрузить</span>
          </b-button>
        </div>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal,
  BOverlay,
  BButton,
  BImg,
  BBadge,
  BFormSelect,
  BFormCheckbox,
  BFormFile,
  BSpinner,
} from 'bootstrap-vue'
import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

// Общая константа типов постера: используется и в карусели, и в подборе, и в загрузке
const POSTER_TYPE_OPTIONS = [
  { value: null, text: 'Обычный' },
  { value: 'minimalistic', text: 'Минималистичный' },
  { value: 'alternative', text: 'Альтернативный' },
  { value: 'foreign', text: 'Иностранный' },
]

const SOURCE_LABELS = {
  kp: 'Кинопоиск',
  tmdb: 'TMDb',
  fanart: 'Fanart.tv',
  imdb: 'IMDb',
  google: 'Google',
  manual: 'Ручная загрузка',
}

const SOURCE_BUTTONS = [
  { value: 'kp', label: 'Кинопоиск', icon: 'FilmIcon' },
  { value: 'tmdb', label: 'TMDb', icon: 'TvIcon' },
  { value: 'fanart', label: 'Fanart.tv', icon: 'ImageIcon' },
  { value: 'imdb', label: 'IMDb', icon: 'GlobeIcon' },
  { value: 'google', label: 'Google', icon: 'SearchIcon' },
]

export default {
  name: 'MoviePostersModal',
  components: {
    BModal,
    BOverlay,
    BButton,
    BImg,
    BBadge,
    BFormSelect,
    BFormCheckbox,
    BFormFile,
    BSpinner,
  },
  props: {
    movieId: { type: [String, Number], required: true },
    visible: { type: Boolean, default: false },
  },
  model: { prop: 'visible', event: 'change' },
  setup(props, { emit }) {
    const toast = useToast()

    const posters = ref([])
    const isLoading = ref(false)
    const viewIndex = ref(0)
    const isDownloading = ref(false)
    const isDeleting = ref(false)

    const previewSource = ref(null)
    const isLoadingPreview = ref(false)
    const previewItems = ref([])
    const selectedPreviewIndexes = ref([])
    const addType = ref(null)
    const isAddingPosters = ref(false)

    const uploadFile = ref(null)
    const uploadType = ref(null)
    const isUploading = ref(false)

    const posterTypeOptions = POSTER_TYPE_OPTIONS
    const sourceButtons = SOURCE_BUTTONS
    const sourceLabel = source => SOURCE_LABELS[source] || source

    const currentPoster = computed(() => posters.value[viewIndex.value] || null)

    // На узких экранах в ленте видно меньше соседей (радиус окна вместо 2 — 1)
    const isMobileStrip = ref(false)
    const updateIsMobileStrip = () => {
      isMobileStrip.value = window.innerWidth < 576
    }

    // Окно ленты вокруг активного индекса: до 2 соседей с каждой стороны, у краёв не зацикливаем — просто меньше соседей
    const visiblePosters = computed(() => {
      const total = posters.value.length
      if (!total) return []
      const radius = isMobileStrip.value ? 1 : 2
      const start = Math.max(0, viewIndex.value - radius)
      const end = Math.min(total - 1, viewIndex.value + radius)
      return posters.value.slice(start, end + 1).map((poster, offset) => ({
        poster,
        index: start + offset,
      }))
    })

    const setActivePoster = index => {
      viewIndex.value = index
    }

    const goPrev = () => {
      if (viewIndex.value > 0) viewIndex.value -= 1
    }
    const goNext = () => {
      if (viewIndex.value < posters.value.length - 1) viewIndex.value += 1
    }

    const onKeydown = event => {
      if (event.key === 'ArrowLeft') {
        goPrev()
      } else if (event.key === 'ArrowRight') {
        goNext()
      }
    }
    const onShown = () => {
      window.addEventListener('keydown', onKeydown)
      updateIsMobileStrip()
      window.addEventListener('resize', updateIsMobileStrip)
    }
    const onHidden = () => {
      window.removeEventListener('keydown', onKeydown)
      window.removeEventListener('resize', updateIsMobileStrip)
    }

    const loadPosters = () => {
      isLoading.value = true
      store.dispatch('movies/fetchMoviePosters', { id: props.movieId })
        .then(response => {
          posters.value = response.data.data
          viewIndex.value = 0
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при загрузке постеров',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    watch(() => props.visible, visible => {
      if (visible) {
        loadPosters()
        return
      }
      posters.value = []
      viewIndex.value = 0
      previewSource.value = null
      previewItems.value = []
      selectedPreviewIndexes.value = []
      addType.value = null
      uploadFile.value = null
      uploadType.value = null
    })

    // :value (не v-model) — нужен доступ к предыдущему значению для отката при ошибке
    const updatePosterType = (poster, value) => {
      const previousType = poster.type
      poster.type = value
      store.dispatch('movies/updateMoviePoster', {
        id: props.movieId,
        posterId: poster.id,
        data: { type: value },
      })
        .then(response => {
          Object.assign(poster, response.data.data)
          emit('updated', posters.value)
        })
        .catch(() => {
          poster.type = previousType
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при изменении типа постера',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    // Ручные признаки содержимого постера; при ошибке откатываем переключатель (паттерн updatePosterFlag из MovieDetail)
    const updatePosterFlag = (poster, field, value) => {
      store.dispatch('movies/updateMoviePoster', {
        id: props.movieId,
        posterId: poster.id,
        data: { [field]: value },
      })
        .then(response => {
          Object.assign(poster, response.data.data)
          emit('updated', posters.value)
        })
        .catch(() => {
          poster[field] = !value
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при сохранении признака постера',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const downloadCurrentPoster = () => {
      const poster = currentPoster.value
      if (!poster) return
      isDownloading.value = true
      store.dispatch('movies/downloadMoviePoster', {
        id: props.movieId,
        posterId: poster.id,
        source: poster.source,
      })
        .then(response => {
          Object.assign(poster, response.data.data)
          emit('updated', posters.value)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Постер скачан на S3',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Ошибка при скачивании постера',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isDownloading.value = false
        })
    }

    const deleteCurrentPoster = () => {
      const poster = currentPoster.value
      if (!poster) return
      if (!window.confirm('Удалить постер? Файл будет удалён и с S3.')) return
      isDeleting.value = true
      store.dispatch('movies/deleteMoviePoster', { id: props.movieId, posterId: poster.id })
        .then(() => {
          posters.value = posters.value.filter(existing => existing.id !== poster.id)
          if (viewIndex.value >= posters.value.length) {
            viewIndex.value = Math.max(0, posters.value.length - 1)
          }
          emit('updated', posters.value)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Постер удалён',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при удалении постера',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isDeleting.value = false
        })
    }

    const fetchPreview = source => {
      isLoadingPreview.value = true
      previewSource.value = source
      previewItems.value = []
      selectedPreviewIndexes.value = []
      // Google ищет по запросу «Minimalistic poster <название>» — предвыбираем соответствующий тип
      if (source === 'google') addType.value = 'minimalistic'
      store.dispatch('movies/previewMoviePosters', { id: props.movieId, source })
        .then(response => {
          previewItems.value = response.data.data
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при загрузке превью постеров',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isLoadingPreview.value = false
        })
    }

    const isPreviewSelected = index => selectedPreviewIndexes.value.includes(index)

    const togglePreviewSelection = index => {
      const item = previewItems.value[index]
      if (!item || item.exists) return
      if (isPreviewSelected(index)) {
        selectedPreviewIndexes.value = selectedPreviewIndexes.value.filter(existing => existing !== index)
      } else {
        selectedPreviewIndexes.value = [...selectedPreviewIndexes.value, index]
      }
    }

    const addSelectedPosters = () => {
      if (!selectedPreviewIndexes.value.length) return
      const postersPayload = selectedPreviewIndexes.value.map(index => {
        const item = previewItems.value[index]
        return {
          url: item.url,
          preview_url: item.preview_url,
          external_id: item.external_id,
          width: item.width,
          height: item.height,
        }
      })

      isAddingPosters.value = true
      store.dispatch('movies/addMoviePosters', {
        id: props.movieId,
        source: previewSource.value,
        type: addType.value,
        posters: postersPayload,
      })
        .then(response => {
          const added = response.data.data
          posters.value = [...posters.value, ...added]
          // Дубли по external_id бэкенд молча пропускает — помечаем «В базе» только реально добавленные
          const addedExternalIds = added.map(poster => poster.external_id).filter(externalId => externalId !== null)
          previewItems.value = previewItems.value.map(item => (
            item.external_id !== null && addedExternalIds.includes(item.external_id)
              ? { ...item, exists: true }
              : item
          ))
          selectedPreviewIndexes.value = []
          emit('updated', posters.value)
          toast({
            component: ToastificationContent,
            props: {
              title: `Добавлено постеров: ${added.length}`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при добавлении постеров',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isAddingPosters.value = false
        })
    }

    const uploadPoster = () => {
      if (!uploadFile.value) return
      const formData = new FormData()
      formData.append('file', uploadFile.value)
      if (uploadType.value) formData.append('type', uploadType.value)

      isUploading.value = true
      store.dispatch('movies/uploadMoviePoster', { id: props.movieId, formData })
        .then(response => {
          posters.value = [...posters.value, response.data.data]
          uploadFile.value = null
          uploadType.value = null
          emit('updated', posters.value)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Постер загружен',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Ошибка при загрузке постера',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isUploading.value = false
        })
    }

    return {
      posters,
      isLoading,
      viewIndex,
      currentPoster,
      visiblePosters,
      setActivePoster,
      goPrev,
      goNext,
      onShown,
      onHidden,
      posterTypeOptions,
      sourceButtons,
      sourceLabel,
      updatePosterType,
      updatePosterFlag,
      isDownloading,
      downloadCurrentPoster,
      isDeleting,
      deleteCurrentPoster,
      previewSource,
      isLoadingPreview,
      previewItems,
      selectedPreviewIndexes,
      isPreviewSelected,
      togglePreviewSelection,
      fetchPreview,
      addType,
      isAddingPosters,
      addSelectedPosters,
      uploadFile,
      uploadType,
      isUploading,
      uploadPoster,
    }
  },
}
</script>

<style lang="scss" scoped>
.poster-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  overflow: hidden;
  min-height: 380px;
}

.poster-strip-item {
  flex-shrink: 0;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.3s ease;

  img {
    display: block;
    height: 220px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    border-radius: 0.5rem;
    transition: height 0.3s ease;
  }

  &:hover {
    opacity: 0.85;
  }
}

.poster-strip-item-active {
  opacity: 1;

  img {
    height: 360px;
  }

  &:hover {
    opacity: 1;
  }
}

@media (max-width: 576px) {
  .poster-strip {
    min-height: 260px;
  }

  .poster-strip-item img {
    height: 150px;
  }

  .poster-strip-item-active img {
    height: 240px;
  }
}

.posters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;
}

.poster-item-wrapper {
  position: relative;
  aspect-ratio: 2 / 3;
}

.poster-item {
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  border: 1px solid #ebe9f1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;

  .poster-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    &.grayscale {
      filter: grayscale(1);
      opacity: 0.5;
    }
  }
}

.poster-item-selected {
  border: 2px solid #7367f0 !important;
}

.poster-selected-check {
  position: absolute;
  top: 5px;
  left: 5px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #7367f0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.poster-corner-badge {
  position: absolute;
  top: 5px;
  left: 5px;
  pointer-events: none;
}

.poster-lang-badge {
  position: absolute;
  bottom: 5px;
  right: 5px;
  pointer-events: none;
}

.poster-file-input {
  max-width: 260px;
}
</style>
